"use client";

import { c as _c } from "react/compiler-runtime";
import { ShopCartLinePricing, cartCanShip, getShopCartPricingPreview } from "../cart-line-utils";
import { renderCartLine } from "../cart-renderers";
import type { ShopPromotionMessage } from "../promotion-messaging";
import React from "react";
import { Button } from "@/components/ui";
import QuantityStepper from "@/components/ui/quantity-stepper";
import { useShopCatalog } from "@/contexts/shop-catalog-provider";
import { formatCurrency } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { ItemType } from "@/types/enums/item-type";
import { ShopFulfillmentMethod } from "@/types/enums/shop-fulfillment-method";
import { ShopCartLine } from "@/types/shop";
import PromotionProgress from "./promotion-progress";
interface CartSummaryPanelProps {
  lines: ShopCartLine[];
  onUpdateQuantity: (lineId: string, quantity: number) => void;
  onRemoveLine: (lineId: string) => void;
  onReplaceLine: (lineId: string, line: ShopCartLine) => void;
  onCheckout: () => void;
  promotionMessages?: ShopPromotionMessage[];
  /** The patient's earlier delivery choice; pickup carries no shipping charge. */
  fulfillmentMethod?: ShopFulfillmentMethod | null;
  className?: string;
}
const CartLineRow: React.FC<{
  line: ShopCartLine;
  onUpdateQuantity: (lineId: string, quantity: number) => void;
  onRemoveLine: (lineId: string) => void;
  onReplaceLine: (lineId: string, line: ShopCartLine) => void;
  pricing: ShopCartLinePricing;
}> = t0 => {
  const $ = _c(31);
  const {
    line,
    onUpdateQuantity,
    onRemoveLine,
    onReplaceLine,
    pricing
  } = t0;
  let t1;
  if ($[0] !== line.lineId || $[1] !== onReplaceLine) {
    t1 = updated => onReplaceLine(line.lineId, updated);
    $[0] = line.lineId;
    $[1] = onReplaceLine;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== line.lineId || $[4] !== onRemoveLine) {
    t2 = () => onRemoveLine(line.lineId);
    $[3] = line.lineId;
    $[4] = onRemoveLine;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== line || $[7] !== t1 || $[8] !== t2) {
    t3 = renderCartLine(line, t1, t2);
    $[6] = line;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== line.itemType || $[11] !== line.lineId || $[12] !== line.quantity || $[13] !== onUpdateQuantity) {
    t4 = line.itemType === ItemType.MEMBERSHIP ? <span className="text-caption font-semibold text-foreground-secondary">Quantity 1</span> : <QuantityStepper value={line.quantity} onChange={n => onUpdateQuantity(line.lineId, n)} />;
    $[10] = line.itemType;
    $[11] = line.lineId;
    $[12] = line.quantity;
    $[13] = onUpdateQuantity;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== pricing.lineTotalCents) {
    t5 = formatCurrency(pricing.lineTotalCents);
    $[15] = pricing.lineTotalCents;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t5) {
    t6 = <span className="text-body-emphasis text-foreground">{t5}</span>;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== pricing.hasPromotion || $[20] !== pricing.originalLineTotalCents) {
    t7 = pricing.hasPromotion && <span className="text-caption text-foreground-secondary line-through">{formatCurrency(pricing.originalLineTotalCents)}</span>;
    $[19] = pricing.hasPromotion;
    $[20] = pricing.originalLineTotalCents;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t6 || $[23] !== t7) {
    t8 = <span className="flex flex-wrap items-baseline justify-end gap-2 tabular-nums">{t6}{t7}</span>;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t4 || $[26] !== t8) {
    t9 = <div className="flex items-center justify-between gap-3 pl-[3.25rem]">{t4}{t8}</div>;
    $[25] = t4;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t3 || $[29] !== t9) {
    t10 = <li className="flex flex-col gap-3 py-5 first:pt-2">{t3}{t9}</li>;
    $[28] = t3;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
};

/**
 * Cart contents rendered as plain content (no card wrapper). Per-type
 * visual identity + edit affordances are delegated to `renderCartLine`;
 * this component owns the generic remove / quantity / subtotal chrome.
 */
const CartSummaryPanel: React.FC<CartSummaryPanelProps> = t0 => {
  const $ = _c(54);
  const {
    lines,
    onUpdateQuantity,
    onRemoveLine,
    onReplaceLine,
    onCheckout,
    promotionMessages: t1,
    fulfillmentMethod,
    className
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const promotionMessages = t2;
  const {
    catalog
  } = useShopCatalog();
  let t10;
  let t11;
  let t12;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== catalog.items || $[3] !== catalog.shipping || $[4] !== className || $[5] !== fulfillmentMethod || $[6] !== lines || $[7] !== onRemoveLine || $[8] !== onReplaceLine || $[9] !== onUpdateQuantity || $[10] !== promotionMessages) {
    t12 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const {
        pricingByLineId,
        shippingFeeCents,
        totalCents: displayTotalCents
      } = getShopCartPricingPreview(lines, catalog.items, catalog.shipping, fulfillmentMethod);
      let t13;
      if ($[21] !== catalog.items || $[22] !== catalog.shipping || $[23] !== fulfillmentMethod || $[24] !== lines) {
        t13 = fulfillmentMethod !== ShopFulfillmentMethod.PICKUP && cartCanShip(lines, catalog.items, catalog.shipping);
        $[21] = catalog.items;
        $[22] = catalog.shipping;
        $[23] = fulfillmentMethod;
        $[24] = lines;
        $[25] = t13;
      } else {
        t13 = $[25];
      }
      const requiresShipping = t13;
      if (lines.length === 0) {
        t12 = null;
        break bb0;
      }
      if ($[26] !== className) {
        t9 = cx("flex flex-col overflow-x-hidden", className);
        $[26] = className;
        $[27] = t9;
      } else {
        t9 = $[27];
      }
      t10 = <ul className="divide-y divide-border">{lines.map(line => <CartLineRow key={line.lineId} line={line} onUpdateQuantity={onUpdateQuantity} onRemoveLine={onRemoveLine} onReplaceLine={onReplaceLine} pricing={pricingByLineId.get(line.lineId)} />)}</ul>;
      if ($[28] !== promotionMessages) {
        t11 = <PromotionProgress messages={promotionMessages} className="mt-5" />;
        $[28] = promotionMessages;
        $[29] = t11;
      } else {
        t11 = $[29];
      }
      t7 = "mt-6 space-y-3 border-t border-border pt-6";
      if ($[30] !== requiresShipping || $[31] !== shippingFeeCents) {
        t8 = requiresShipping && <div className="flex items-center justify-between gap-4 text-body"><dt className="text-foreground-secondary">Shipping</dt><dd className="font-medium tabular-nums text-foreground">{shippingFeeCents > 0 ? formatCurrency(shippingFeeCents) : "Free"}</dd></div>;
        $[30] = requiresShipping;
        $[31] = shippingFeeCents;
        $[32] = t8;
      } else {
        t8 = $[32];
      }
      t5 = "flex items-center justify-between gap-4";
      if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <dt className="text-overline text-foreground-secondary">Total</dt>;
        $[33] = t6;
      } else {
        t6 = $[33];
      }
      t3 = "text-h2 tabular-nums text-foreground";
      t4 = formatCurrency(displayTotalCents);
    }
    $[2] = catalog.items;
    $[3] = catalog.shipping;
    $[4] = className;
    $[5] = fulfillmentMethod;
    $[6] = lines;
    $[7] = onRemoveLine;
    $[8] = onReplaceLine;
    $[9] = onUpdateQuantity;
    $[10] = promotionMessages;
    $[11] = t10;
    $[12] = t11;
    $[13] = t12;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t10 = $[11];
    t11 = $[12];
    t12 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
    t8 = $[19];
    t9 = $[20];
  }
  if (t12 !== Symbol.for("react.early_return_sentinel")) {
    return t12;
  }
  let t13;
  if ($[34] !== t3 || $[35] !== t4) {
    t13 = <dd className={t3}>{t4}</dd>;
    $[34] = t3;
    $[35] = t4;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t13 || $[38] !== t5 || $[39] !== t6) {
    t14 = <div className={t5}>{t6}{t13}</div>;
    $[37] = t13;
    $[38] = t5;
    $[39] = t6;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t14 || $[42] !== t7 || $[43] !== t8) {
    t15 = <dl className={t7}>{t8}{t14}</dl>;
    $[41] = t14;
    $[42] = t7;
    $[43] = t8;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span>Continue to payment</span>;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== onCheckout) {
    t17 = <Button variant="primary" className="mt-6 w-full cursor-pointer py-3" onClick={onCheckout}>{t16}</Button>;
    $[46] = onCheckout;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t10 || $[49] !== t11 || $[50] !== t15 || $[51] !== t17 || $[52] !== t9) {
    t18 = <div className={t9}>{t10}{t11}{t15}{t17}</div>;
    $[48] = t10;
    $[49] = t11;
    $[50] = t15;
    $[51] = t17;
    $[52] = t9;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  return t18;
};
export default CartSummaryPanel;